import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiLogin, apiRegister, apiRefresh, apiLogout, setAccessToken, setOnAuthError } from "../api";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
  }, []);

  // Wire up the 401 handler so protected apiFetch calls clear state
  useEffect(() => {
    setOnAuthError(clearSession);
  }, [clearSession]);

  // On mount: try to restore session via refresh cookie
  useEffect(() => {
    apiRefresh()
      .then((data) => {
        setAccessToken(data.access_token);
        setUser(data.user);
      })
      .catch(() => {
        // No valid session -- that's fine
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await apiLogin(email, password);
    setAccessToken(data.access_token);
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (email, password, displayName) => {
    return apiRegister(email, password, displayName);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout().catch(() => {});
    clearSession();
  }, [clearSession]);

  const value = { user, loading, login, register, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
